import React, { useState, useMemo } from 'react';
import { Button } from '@appkit4/react-components/button';
import Modal from '../../components/reusables/Modal';
import { Search } from '@appkit4/react-components/search';
import Panel from '../../components/reusables/Panel';
import { Loading } from '@appkit4/react-components/loading';
import '../SharedPage.scss';
import TaxPoolsForm, { TaxPoolsFormData } from '../../components/TaxPoolsForm';
import { useEngagement } from '../../context/EngagementContext';
import {
    useGetPools,
    useCreatePool,
    useUpdatePool,
    useDeletePool,
    getGetPoolsQueryKey,
} from '../../api/generated/endpoint';
import { useQueryClient } from '@tanstack/react-query';
import type { PoolResponse } from '../../api/generated/models';
import useToast from '../../helpers/useToast';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';

function TaxPools() {
    const queryClient = useQueryClient();
    const { activeTimeId, selectedEntityId } = useEngagement();
    const showToast = useToast();
    const showGenericErrorToast = useGenericErrorToast();

    const { data: pools, isLoading } = useGetPools(
        { entityId: selectedEntityId, timeId: activeTimeId },
        { query: { enabled: !!selectedEntityId && !!activeTimeId } }
    );

    const [searchTerm, setSearchTerm] = useState('');

    const filteredPools = useMemo(() => {
        if (!pools || !Array.isArray(pools)) return [];
        if (!searchTerm) return pools as PoolResponse[];
        const term = searchTerm.toLowerCase();
        return (pools as PoolResponse[]).filter(
            (p) =>
                p.poolShortName?.toLowerCase().includes(term) ||
                p.poolLongName?.toLowerCase().includes(term)
        );
    }, [pools, searchTerm]);

    // Modal state
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [addFormData, setAddFormData] = useState<TaxPoolsFormData>({
        shortName: '',
        description: '',
        calcType: '',
        rate: '',
    });

    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editFormData, setEditFormData] = useState<TaxPoolsFormData>({
        shortName: '',
        description: '',
        calcType: '',
        rate: '',
    });
    const [selectedPoolId, setSelectedPoolId] = useState<number | null>(null);
    const [selectedPoolIsDefault, setSelectedPoolIsDefault] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

    const isAddFormValid =
        !!addFormData.shortName &&
        !!addFormData.description &&
        !!addFormData.calcType &&
        !!addFormData.rate;

    const isEditFormValid =
        !!editFormData.shortName &&
        !!editFormData.description &&
        !!editFormData.calcType &&
        !!editFormData.rate;

    // Mutations
    const { mutate: createPool } = useCreatePool({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: getGetPoolsQueryKey({
                        entityId: selectedEntityId,
                        timeId: activeTimeId,
                    }),
                });
                setIsAddModalVisible(false);
                setAddFormData({
                    shortName: '',
                    description: '',
                    calcType: '',
                    rate: '',
                });
                showToast('Success', 'Pool created successfully', 'success');
            },
            onError: (error) => showGenericErrorToast(error),
        },
    });

    const { mutate: updatePool } = useUpdatePool({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: getGetPoolsQueryKey({
                        entityId: selectedEntityId,
                        timeId: activeTimeId,
                    }),
                });
                setIsEditModalVisible(false);
                setSelectedPoolId(null);
                showToast('Success', 'Pool updated successfully', 'success');
            },
            onError: (error) => showGenericErrorToast(error),
        },
    });

    const { mutate: deletePool } = useDeletePool({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: getGetPoolsQueryKey({
                        entityId: selectedEntityId,
                        timeId: activeTimeId,
                    }),
                });
                setIsDeleteModalVisible(false);
                setSelectedPoolId(null);
                showToast('Success', 'Pool deleted successfully', 'success');
            },
            onError: (error) => {
                setIsDeleteModalVisible(false);
                showGenericErrorToast(error);
            },
        },
    });

    // ---- Add Pool ----
    const openAddModal = () => {
        setAddFormData({
            shortName: '',
            description: '',
            calcType: '',
            rate: '',
        });
        setIsAddModalVisible(true);
    };

    const handleAddFormChange = (
        field: keyof TaxPoolsFormData,
        value: string | number
    ) => {
        setAddFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleAddFormSubmit = () => {
        if (
            !addFormData.shortName ||
            !addFormData.calcType ||
            !selectedEntityId ||
            !activeTimeId
        )
            return;

        createPool({
            data: {
                entityId: selectedEntityId,
                timeId: activeTimeId,
                poolShortName: addFormData.shortName,
                poolLongName: addFormData.description,
                rate: Number(addFormData.rate),
                isDefaultHMRC: false,
                calcTypeId: Number(addFormData.calcType),
            },
        });
    };

    // ---- Edit Pool ----
    const openEditModal = (pool: PoolResponse) => {
        setSelectedPoolId(pool.poolID ?? null);
        setSelectedPoolIsDefault(pool.isDefaultHMRC ?? false);
        setEditFormData({
            shortName: pool.poolShortName ?? '',
            description: pool.poolLongName ?? '',
            calcType: String(pool.calcTypeID ?? ''),
            rate: String(pool.rate ?? 0),
        });
        setIsEditModalVisible(true);
    };

    const handleEditFormChange = (
        field: keyof TaxPoolsFormData,
        value: string | number
    ) => {
        setEditFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleEditFormSubmit = () => {
        if (
            !selectedPoolId ||
            !editFormData.shortName ||
            !editFormData.calcType ||
            !selectedEntityId ||
            !activeTimeId
        )
            return;

        updatePool({
            poolId: selectedPoolId,
            data: {
                entityId: selectedEntityId,
                timeId: activeTimeId,
                poolShortName: editFormData.shortName,
                poolLongName: editFormData.description,
                rate: Number(editFormData.rate),
                calcTypeId: Number(editFormData.calcType),
            },
        });
    };

    // ---- Delete Pool ----
    const openDeleteModal = (pool: PoolResponse) => {
        setSelectedPoolId(pool.poolID ?? null);
        setIsDeleteModalVisible(true);
    };

    const confirmDelete = () => {
        if (!selectedPoolId || !selectedEntityId || !activeTimeId) return;
        deletePool({
            poolId: selectedPoolId,
            params: { entityId: selectedEntityId, timeId: activeTimeId },
        });
    };

    if (!selectedEntityId || !activeTimeId) {
        return (
            <div className="poolsMain">
                <p>Select an entity and time period to view pools.</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div
                className="poolsMain"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '2rem',
                }}
            >
                <Loading loadingType="circular" indeterminate />
            </div>
        );
    }

    return (
        <div className="poolsMain">
            <div className="poolsHeader">
                <h3 className="poolsMainLabel">Pools</h3>
                <div className="addPoolButton">
                    <Button
                        onClick={openAddModal}
                        kind="primary"
                        icon="icon-plus-outline"
                    >
                        Add Pool
                    </Button>
                </div>
            </div>
            <div className="searchPools">
                <Search
                    className="px-0"
                    onChange={(value: string) => setSearchTerm(value)}
                    searchValue={searchTerm}
                />
            </div>
            <div className="poolsPanels">
                {filteredPools.map((pool) => (
                    <Panel
                        key={pool.poolID}
                        icon="Appkit4-icon icon-archive-outline"
                        shortName={pool.poolShortName ?? ''}
                        description={pool.poolLongName ?? ''}
                        calculationType={pool.accountingMethodName ?? ''}
                        percentage={`${pool.rate ?? 0}%`}
                        onEdit={() => openEditModal(pool)}
                        onDelete={
                            pool.isDefaultHMRC
                                ? undefined
                                : () => openDeleteModal(pool)
                        }
                    />
                ))}
            </div>

            {/* Add Pool Modal */}
            <Modal
                visible={isAddModalVisible}
                onClose={() => setIsAddModalVisible(false)}
                title="Add Pool"
            >
                <div className="poolsModal">
                    <div className="tax-pools-modal-body">
                        <TaxPoolsForm
                            formData={addFormData}
                            onChange={handleAddFormChange}
                        />
                    </div>
                    <div className="tax-pools-modal-actions">
                        <Button
                            onClick={handleAddFormSubmit}
                            kind="primary"
                            disabled={!isAddFormValid}
                        >
                            Add Pool
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Edit Pool Modal */}
            <Modal
                visible={isEditModalVisible}
                onClose={() => {
                    setIsEditModalVisible(false);
                    setSelectedPoolId(null);
                }}
                title="Edit Pool"
            >
                <div className="poolsModal">
                    <div className="tax-pools-modal-body">
                        <TaxPoolsForm
                            formData={editFormData}
                            onChange={handleEditFormChange}
                            isDefaultHMRC={selectedPoolIsDefault}
                        />
                        <div className="tax-pools-modal-actions">
                            <Button
                                onClick={handleEditFormSubmit}
                                kind="primary"
                                disabled={!isEditFormValid}
                            >
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                visible={isDeleteModalVisible}
                onClose={() => {
                    setIsDeleteModalVisible(false);
                    setSelectedPoolId(null);
                }}
                title="Delete Pool"
            >
                <div className="tax-pools-delete-body">
                    <p>Are you sure you want to delete this pool?</p>
                    <div className="tax-pools-modal-actions">
                        <Button
                            onClick={() => {
                                setIsDeleteModalVisible(false);
                                setSelectedPoolId(null);
                            }}
                            kind="secondary"
                        >
                            Cancel
                        </Button>
                        <Button onClick={confirmDelete} kind="primary">
                            Delete
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default TaxPools;
